import { useEffect, useRef } from "react";
import { useAuth } from "./useAuth";
import { authService } from "@/services/authService";
import { authLogger } from "@/utils/logger";

export const useTokenMonitor = () => {
  const { isAuthenticated, revalidateAuth } = useAuth();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!isAuthenticated) {
      // Limpar interval se não estiver autenticado
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Função para atualizar última atividade
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
    };

    // Eventos que indicam atividade do usuário
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    // Adicionar listeners de atividade
    events.forEach((event) => {
      document.addEventListener(event, updateActivity, true);
    });

    // Verificação periódica do token
    intervalRef.current = setInterval(async () => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;

      // Se o usuário esteve inativo por mais de 30 minutos, não verificar
      if (timeSinceLastActivity > 30 * 60 * 1000) {
        authLogger.info("🛌 Usuário inativo, pulando verificação de token");
        return;
      }

      try {
        authLogger.info("🔍 Verificando validade do token...");
        const isValid = await authService.validateToken();

        if (!isValid) {
          authLogger.info("⚠️ Token inválido detectado, revalidando...");
          revalidateAuth();
        } else {
          authLogger.info("✅ Token ainda válido");
        }
      } catch (error) {
        authLogger.error("❌ Erro na verificação do token:", error);
        // Em caso de erro de rede, não fazer logout imediatamente
        // O interceptor do axios vai lidar com isso
      }
    }, 2 * 60 * 1000); // Verificar a cada 2 minutos

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      events.forEach((event) => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, [isAuthenticated, revalidateAuth]);

  // Listener para mudanças de visibilidade da página
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible" && isAuthenticated) {
        authLogger.info("👁️ Página ficou visível, verificando token...");

        try {
          const isValid = await authService.validateToken();
          if (!isValid) {
            authLogger.info("⚠️ Token inválido após página ficar visível");
            revalidateAuth();
          }
        } catch (error) {
          authLogger.error(
            "❌ Erro ao verificar token após visibilidade:",
            error
          );
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, revalidateAuth]);

  // Listener para eventos de foco da janela
  useEffect(() => {
    const handleFocus = async () => {
      if (isAuthenticated) {
        authLogger.info("🎯 Janela recebeu foco, verificando token...");

        try {
          const isValid = await authService.validateToken();
          if (!isValid) {
            authLogger.info("⚠️ Token inválido após foco da janela");
            revalidateAuth();
          }
        } catch (error) {
          authLogger.error("❌ Erro ao verificar token após foco:", error);
        }
      }
    };

    window.addEventListener("focus", handleFocus);

    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [isAuthenticated, revalidateAuth]);
};
